import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/useApp.js';
import { 
  User, 
  MapPin, 
  GraduationCap, 
  DollarSign, 
  FileText, 
  Mail,
  Edit,
  Save,
  X,
  Camera
} from 'lucide-react';

const LawyerProfile = () => {
  const { language, user, lawyers } = useApp();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || 'Lawyer Name',
    specialization: 'Criminal Law, Civil Law',
    location: 'Mumbai, Maharashtra',
    experience: '5 years',
    education: [{ qualification: 'LL.B.', degreeCollege: 'Law College' }],
    consultationFee: '₹2000',
    bio: 'Experienced lawyer specializing in criminal and civil law with a track record of successful cases.',
    email: user?.email || 'lawyer@example.com',
    phone: '+91 98765 43210'
  });

  const translations = {
    english: {
      title: 'Lawyer Profile',
      editProfile: 'Edit Profile',
      saveChanges: 'Save Changes',
      cancel: 'Cancel',
      personalInfo: 'Personal Information',
      professionalInfo: 'Professional Information',
      contactInfo: 'Contact Information',
      fullName: 'Full Name',
      specialization: 'Specialization',
      location: 'Location',
      experience: 'Years of Experience',
      education: 'Education',
      consultationFee: 'Consultation Fee',
      bio: 'Professional Bio',
      email: 'Email',
      phone: 'Phone Number',
      profilePicture: 'Profile Picture',
      changePhoto: 'Change Photo'
    },
    hindi: {
      title: 'वकील प्रोफाइल',
      editProfile: 'प्रोफाइल संपादित करें',
      saveChanges: 'परिवर्तन सहेजें',
      cancel: 'रद्द करें',
      personalInfo: 'व्यक्तिगत जानकारी',
      professionalInfo: 'पेशेवर जानकारी',
      contactInfo: 'संपर्क जानकारी',
      fullName: 'पूरा नाम',
      specialization: 'विशेषज्ञता',
      location: 'स्थान',
      experience: 'अनुभव के वर्ष',
      education: 'शिक्षा',
      consultationFee: 'परामर्श शुल्क',
      bio: 'पेशेवर जीवनी',
      email: 'ईमेल',
      phone: 'फोन नंबर',
      profilePicture: 'प्रोफाइल फोटो',
      changePhoto: 'फोटो बदलें'
    }
  };

  const t = translations[language] || translations.english;

  const handleSave = () => {
    // Here you would typically save the profile data
    setIsEditing(false);
    // Set flag that lawyer profile is created (for auth navigation)
    localStorage.setItem('lawyerProfileCreated', 'true');
    // Set flag that lawyer profile is complete to prevent redirect back to profile creation
    localStorage.setItem('isProfileComplete', 'true');
    // You could add a toast notification here
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original values
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-lavender-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {t.title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage your professional profile
              </p>
            </div>
            <div className="flex gap-3">
              {!isEditing ? (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsEditing(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  {t.editProfile}
                </motion.button>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSave}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {t.saveChanges}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleCancel}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t.cancel}
                  </motion.button>
                </>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Picture Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-1"
          >
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 dark:border-gray-700/20">
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-4xl font-bold mx-auto">
                    {profileData.fullName.charAt(0)}
                  </div>
                  {isEditing && (
                    <button className="absolute bottom-0 right-0 bg-white dark:bg-gray-700 p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-200">
                      <Camera className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {profileData.fullName}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {profileData.specialization}
                </p>
                {isEditing && (
                  <button className="text-purple-600 dark:text-purple-400 hover:underline font-medium transition-colors duration-200">
                    {t.changePhoto}
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Profile Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Personal Information */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 dark:border-gray-700/20">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <User className="w-5 h-5 text-purple-500" />
                {t.personalInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.fullName}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">
                      {profileData.fullName}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.location}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.location}
                      onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      {profileData.location}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 dark:border-gray-700/20">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <GraduationCap className="w-5 h-5 text-purple-500" />
                {t.professionalInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.specialization}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.specialization}
                      onChange={(e) => setProfileData({...profileData, specialization: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">
                      {profileData.specialization}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.experience}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.experience}
                      onChange={(e) => setProfileData({...profileData, experience: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">
                      {profileData.experience}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.education}
                  </label>
                  {isEditing ? (
                    <div className="space-y-3">
                      {profileData.education?.map((edu, index) => (
                        <div key={index} className="space-y-2">
                          <input
                            type="text"
                            value={edu.qualification || ''}
                            onChange={(e) => {
                              const newEducation = [...(profileData.education || [])];
                              newEducation[index] = { ...newEducation[index], qualification: e.target.value };
                              setProfileData({...profileData, education: newEducation});
                            }}
                            placeholder="Qualification"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="text"
                            value={edu.degreeCollege || ''}
                            onChange={(e) => {
                              const newEducation = [...(profileData.education || [])];
                              newEducation[index] = { ...newEducation[index], degreeCollege: e.target.value };
                              setProfileData({...profileData, education: newEducation});
                            }}
                            placeholder="College/University"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newEducation = [...(profileData.education || []), { qualification: '', degreeCollege: '' }];
                          setProfileData({...profileData, education: newEducation});
                        }}
                        className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 hover:border-purple-500 hover:text-purple-500 transition-colors"
                      >
                        + Add Education
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {profileData.education?.map((edu, index) => (
                        <div key={index} className="text-gray-900 dark:text-white">
                          <p className="font-medium">{edu.qualification}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{edu.degreeCollege}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.consultationFee}
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profileData.consultationFee}
                      onChange={(e) => setProfileData({...profileData, consultationFee: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      {profileData.consultationFee}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.bio}
                </label>
                {isEditing ? (
                  <textarea
                    value={profileData.bio}
                    onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                ) : (
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    {profileData.bio}
                  </p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/20 dark:border-gray-700/20">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
                <Mail className="w-5 h-5 text-purple-500" />
                {t.contactInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.email}
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">
                      {profileData.email}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t.phone}
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  ) : (
                    <p className="text-gray-900 dark:text-white font-medium">
                      {profileData.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LawyerProfile;
